<script setup lang="ts">
//#region import
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useApi } from '@/api';
import { useSessionAction } from '@/composables/useSessionAction';
import { usePopupManager } from '@/components/PopupManager.vue';
import { useTypedStore } from '@/store';
import type { GetControlTowerCustomerCurrenciesResponse } from '@/types/openapi-ts';
//#endregion

//#region const
const fields = [
  {
    name: 'code',
    width: '50',
    align: 'left',
    resizable: true,
  },
  {
    name: 'name',
    minWidth: '192',
    width: '500',
    align: 'left',
    resizable: true,
  },
  {
    name: 'type',
    align: 'left',
    resizable: false,
    class: 'settings-currencies__table-value_capitalize',
  },
];
//#endregion

//#region composables
const { onSessionUnlock } = useSessionAction();
const { t } = useI18n();
const api = useApi();
const store = useTypedStore();
const popupManager = usePopupManager();
//#endregion

//#region state
const tableData = ref<GetControlTowerCustomerCurrenciesResponse['payload']>([]);
//#endregion

//#region computed
const excluded = computed(() => {
  return tableData.value.map((el) => el.code);
});
//#endregion

//#region methods
const getCustomerCurrencies = () => {
  api.getCustomerCurrencies().then((res) => {
    tableData.value = res.data.payload;
  });
};
const confirmSuccess = () => {
  getCustomerCurrencies();
  store.dispatch('misc/MISC_GET_CUSTOMER_CURRENCIES');
};

const handleAddCurrencyPopup = () => {
  popupManager.addCurrency.open({
    data: {
      excluded: excluded.value,
    },
    listeners: {
      success: () => {
        getCustomerCurrencies();
      },
    },
  });
};

const handleSetAsDefault = (row: GetControlTowerCustomerCurrenciesResponse['payload'][number]) => {
  const confirmPopup = popupManager.confirm.open({
    data: {
      title: t('settings.currencies.confirm.setAsDefaultCurrency'),
      message: t('settings.currencies.confirm.confirmSetAsDefault', { currency: row.name }),
      bodyIcon: 'house-circle-check',
      confirmTitle: t('settings.currencies.confirm.setAsDefault'),
    },
    listeners: {
      success: async () => {
        const context = await confirmPopup;
        try {
          await api.putCurrencySetDefault({ code: row.code });
          confirmSuccess();
          context.handleClose();
        } catch (error) {
          window.console.error(error);
        }
      },
    },
  });
};

const handleDelete = (row: GetControlTowerCustomerCurrenciesResponse['payload'][number]) => {
  const confirmPopup = popupManager.confirm.open({
    data: {
      title: t('settings.currencies.confirm.deleteCurrency'),
      message: t('settings.currencies.confirm.confirmDelete', { name: row.name }),
      bodyIcon: 'exclamation-triangle',
      bodyIconColor: 'var(--text-negative)',
      confirmTitle: t('settings.tokens.delete'),
      confirmType: 'negative',
    },
    listeners: {
      success: async () => {
        const context = await confirmPopup;
        try {
          await api.deleteCurrency(row.code );
          confirmSuccess();
          context.handleClose();
        } catch (error) {
          window.console.error(error);
        }
      },
    },
  });
};
//#endregion

//#region hooks
onSessionUnlock.value = getCustomerCurrencies;
getCustomerCurrencies();
//#endregion
</script>

<template>
  <div class="settings-currencies">
    <UiToolbar>
      <template v-slot:title>
        {{ $t('settings.currencies.title') }}
      </template>
      <template v-slot:actions>
        <UiButton
          filled
          icon="plus"
          @click="handleAddCurrencyPopup"
        >
          {{ $t('settings.currencies.addCurrency') }}
        </UiButton>
      </template>
    </UiToolbar>
    <UiTableNew
      :fields="fields"
      :data="tableData"
      class="settings-currencies__table"
      is-static
      lazy-loading
      disable-sort
      i18n-path="settings.currencies"
    >
      <template v-slot:append>
        <ElTableColumn
          align="center"
          width="76"
        >
          <template v-slot:header="scope">
            <div
              :key="scope.$index"
            >
              {{ $t('settings.tokens.actions') }}
            </div>
          </template>
          <template v-slot="scope">
            <ActionIcon
              icon="house-circle-check"
              color="var(--text-primary)"
              :tooltip="$t('settings.systemDomains.confirm.setAsDefault')"
              :disabled="scope.row.is_default"
              @click="handleSetAsDefault(scope.row)"
            />
            <ActionIcon
              icon="trash-alt"
              color="var(--text-negative)"
              :tooltip="$t('crm.buttons.delete')"
              :disabled="scope.row.is_default"
              @click="handleDelete(scope.row)"
            />
          </template>
        </ElTableColumn>
      </template>
    </UiTableNew>
  </div>
</template>

<style lang="scss">
.settings-currencies {
  &__table-value {
    &_capitalize {
      text-transform: capitalize;
    }
  }
}
</style>
